import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext'; // Assuming authContext is at the same level

// 1. Create the Context
const EmailChangeContext = createContext();

// 2. Create the Custom Hook
export const useEmailChange = () => {
    const context = useContext(EmailChangeContext);
    if (!context) {
        throw new Error('useEmailChange must be used within an EmailChangeProvider');
    }
    return context;
};

// 3. Create the Provider Component
export const EmailChangeProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // useAuth provides the authenticated request function and login type
    const { makeAuthenticatedRequest, loginType } = useAuth();

    // API base URL for the entire application
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

    // Determine the correct base URL based on login type (admin or master)
    const getServiceBaseUrl = useCallback(() => {
        if (loginType === 'master') {
            return `${API_BASE_URL}/master/email-change/api/v1/email-change`;
        } else if (loginType === 'admin') {
            return `${API_BASE_URL}/admin/email-change/api/v1/email-change`;
        } else {
            // Throw error if unauthorized user tries to access this
            throw new Error('Only admin and master users can initiate the email change process');
        }
    }, [API_BASE_URL, loginType]);

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const data = await response.json();

        if (!response.ok) {
            // Handle specific backend error codes
            if (response.status === 404) {
                throw new Error(data.error || 'Business registration not found');
            } else if (response.status === 409) {
                throw new Error(data.error || 'Conflict: New email is already registered or no pending change was found.');
            } else if (response.status === 401) {
                throw new Error(data.error || 'Unauthorized: Invalid verification code or access.');
            } else if (response.status === 400) {
                throw new Error(data.error || 'Invalid request data');
            } else if (response.status === 500) {
                throw new Error(data.error || 'Internal server error');
            }

            throw new Error(data.error || data.message || 'Request failed');
        }

        return data;
    };

    // Clear error state
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Validate admin/master access
    const validateAccess = useCallback(() => {
        if (loginType !== 'admin' && loginType !== 'master') {
            throw new Error('Access Denied: You must be an admin or master user to use this service.');
        }
    }, [loginType]);

    // STEP 1: Initiate Email Change (Send Code to Old Email)
    const initiateEmailChange = useCallback(async (registryNumber, newEmail) => {
        validateAccess();

        if (!registryNumber || !newEmail) {
            throw new Error('Business Registry Number and New Email are required.');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const requestBody = {
                business_registry_number: registryNumber,
                new_email: newEmail,
            };

            const response = await makeAuthenticatedRequest(`${baseUrl}/initiate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            // The result includes old_email and a message that the code was sent
            return {
                ...result,
                initiationSuccessful: true,
                message: result.message || `Verification code sent to ${result.old_email}`,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to initiate email change.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // STEP 2: Complete Email Change (Verify Code and Update DB)
    const completeEmailChange = useCallback(async (registryNumber, newEmail, verificationCode) => {
        validateAccess();

        if (!registryNumber || !newEmail || !verificationCode) {
            throw new Error('Registry Number, New Email, and Verification Code are required.');
        }

        if (verificationCode.length !== 6 || !/^\d{6}$/.test(verificationCode)) {
            throw new Error('Verification Code must be a 6-digit number.');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const requestBody = {
                business_registry_number: registryNumber,
                new_email: newEmail,
                verification_code: verificationCode,
            };

            const response = await makeAuthenticatedRequest(`${baseUrl}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            // The result includes old_email, new_email, and a success message
            return {
                ...result,
                completionSuccessful: true,
                message: result.message || `Email successfully updated from ${result.old_email} to ${result.new_email}.`,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to complete email change.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // Health check - adapted from the example provider
    const checkHealth = useCallback(async () => {
        validateAccess();
        try {
            const healthUrl = loginType === 'master'
                ? `${API_BASE_URL}/master/email-change/health`
                : `${API_BASE_URL}/admin/email-change/health`;

            const response = await makeAuthenticatedRequest(healthUrl);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Email Change Context Health check failed';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    }, [makeAuthenticatedRequest, API_BASE_URL, loginType, validateAccess]);


    // Define the context value
    const value = {
        // State
        loading,
        error,

        // Actions
        initiateEmailChange,
        completeEmailChange,
        checkHealth,

        // Utility
        clearError,
        
        // Computed values
        isMasterUser: loginType === 'master',
        isAdminUser: loginType === 'admin',
        hasAccess: loginType === 'admin' || loginType === 'master',
    };

    return (
        <EmailChangeContext.Provider value={value}>
            {children}
        </EmailChangeContext.Provider>
    );
};